import type { BeforeSendFn, PostHogConfig } from "posthog-js";
import { ANALYTICS_CONSENT_VERSION } from "@/lib/analytics-consent";
import { CONTACT_EMAIL, SCHEDULE_VISIO_URL } from "@/lib/constants";
import { stripLocaleFromPathname } from "@/lib/i18n/locale";

const campaignValue = (value: unknown) =>
	typeof value === "string" && /^[a-z0-9][a-z0-9._-]{0,79}$/i.test(value)
		? value
		: undefined;

export const sanitizePageUrl = (value: unknown) => {
	if (typeof value !== "string") return undefined;
	try {
		const url = new URL(value);
		if (
			!/^https?:$/.test(url.protocol) ||
			!/^\/[a-z0-9/_-]*$/i.test(url.pathname)
		)
			return undefined;
		return `${url.origin}${url.pathname}`;
	} catch {
		return undefined;
	}
};

export const referrerOrigin = (value: unknown) => {
	if (typeof value !== "string" || !value) return undefined;
	try {
		const url = new URL(value);
		return /^https?:$/.test(url.protocol) ? url.origin : undefined;
	} catch {
		return undefined;
	}
};

export const contactTarget = (
	href: unknown,
): "calendar" | "email" | undefined => {
	if (typeof href !== "string") return undefined;
	try {
		const url = new URL(href);
		if (
			url.protocol === "mailto:" &&
			url.pathname.toLowerCase() === CONTACT_EMAIL
		)
			return "email";
		const booking = new URL(SCHEDULE_VISIO_URL);
		if (url.origin === booking.origin && url.pathname === booking.pathname)
			return "calendar";
	} catch {
		/* Unrecognized destinations are outside the collection scope. */
	}
	return undefined;
};

export const currentPageProperties = (pageUrl?: unknown) => {
	if (typeof window === "undefined") return {};
	const url = new URL(
		typeof pageUrl === "string" ? pageUrl : window.location.href,
	);
	const params = url.searchParams;
	return {
		$current_url: sanitizePageUrl(url.href),
		referrer_origin: referrerOrigin(document.referrer),
		utm_source: campaignValue(params.get("utm_source")),
		utm_medium: campaignValue(params.get("utm_medium")),
		utm_campaign: campaignValue(params.get("utm_campaign")),
	};
};

/** Rebuild the final SDK payload: default properties and person updates never pass through. */
export const restrictAnalyticsEvent: BeforeSendFn = (event) => {
	if (!event || !["$pageview", "cta_click"].includes(event.event)) return null;
	const props = event.properties;
	const pageUrl = sanitizePageUrl(props.$current_url);
	if (
		!pageUrl ||
		typeof props.distinct_id !== "string" ||
		!/^[a-f0-9-]{36}$/i.test(props.distinct_id)
	)
		return null;
	if (
		event.event === "cta_click" &&
		!["calendar", "email"].includes(props.target)
	)
		return null;
	const properties: Record<string, unknown> = {
		token: props.token,
		distinct_id: props.distinct_id,
		$current_url: pageUrl,
		path: stripLocaleFromPathname(new URL(pageUrl).pathname),
		$process_person_profile: false,
		$geoip_disable: true,
		consent_version: ANALYTICS_CONSENT_VERSION,
	};
	if (props.locale === "fr-FR" || props.locale === "en-US")
		properties.locale = props.locale;
	if (
		typeof props.$session_id === "string" &&
		/^[a-f0-9-]{36}$/i.test(props.$session_id)
	)
		properties.$session_id = props.$session_id;
	const origin = referrerOrigin(props.referrer_origin);
	if (origin) {
		properties.referrer_origin = origin;
		properties.$referrer = origin;
		properties.$referring_domain = new URL(origin).hostname;
	}
	for (const key of ["utm_source", "utm_medium", "utm_campaign"]) {
		const value = campaignValue(props[key]);
		if (value) properties[key] = value;
	}
	if (event.event === "cta_click") {
		properties.target = props.target;
		const placement = campaignValue(props.placement);
		if (placement) properties.placement = placement;
	}
	return {
		uuid: event.uuid,
		event: event.event,
		timestamp: event.timestamp,
		properties,
	};
};

export const LIMITED_ANALYTICS_OPTIONS: Partial<PostHogConfig> = {
	autocapture: false,
	capture_pageview: false,
	capture_pageleave: false,
	capture_exceptions: false,
	capture_performance: false,
	capture_dead_clicks: false,
	enable_heatmaps: false,
	disable_session_recording: true,
	disable_surveys: true,
	disable_conversations: true,
	disable_web_experiments: true,
	disable_product_tours: true,
	disable_external_dependency_loading: true,
	advanced_disable_flags: true,
	advanced_disable_decide: true,
	person_profiles: "never",
	persistence: "memory",
	consent_persistence_name: "analytics-sdk-consent",
	opt_out_capturing_persistence_type: "localStorage",
	opt_out_capturing_by_default: true,
	opt_out_persistence_by_default: true,
	cross_subdomain_cookie: false,
	ip: false,
	save_campaign_params: false,
	save_referrer: false,
	request_batching: false,
};
